"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { X, AlertCircle, ShieldCheck } from "lucide-react"
import { Z } from "@/lib/z-index"
import { useAnimatedClose } from "@/lib/use-animated-close"
import type { CommunityPost } from "@/lib/community/types"
import { ABILITY_TYPE_CONFIG } from "@/lib/community/types"
import { AbilityTypeBadge } from "./ability-type-badge"

interface PaymentConfirmPanelProps {
  post: CommunityPost
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
}

/**
 * PaymentConfirmPanel — payment confirmation modal for paid abilities.
 * Shows price and payment button, then displays insufficient balance error on click.
 */
export function PaymentConfirmPanel({
  post,
  isOpen,
  onClose,
  onConfirm,
}: PaymentConfirmPanelProps) {
  const { closing, requestClose } = useAnimatedClose(onClose, 280)
  const [entered, setEntered] = useState(false)
  const [showError, setShowError] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setEntered(false)
      setShowError(false)
      const raf = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(raf)
    }
  }, [isOpen])

  const handlePay = () => {
    setShowError(true)
  }

  const panelStyle = {
    opacity: closing ? 0 : entered ? 1 : 0,
    transform: closing
      ? "scale(0.96) translateY(8px)"
      : entered
        ? "scale(1) translateY(0)"
        : "scale(0.92) translateY(12px)",
    filter: closing ? "blur(4px)" : entered ? "blur(0)" : "blur(6px)",
    transition: "opacity 280ms, transform 280ms, filter 280ms",
    transitionTimingFunction: closing
      ? "cubic-bezier(0.4, 0, 1, 1)"
      : "cubic-bezier(0.16, 1, 0.3, 1)",
  }

  if (!isOpen) return null

  const typeConfig = ABILITY_TYPE_CONFIG[post.abilityType]
  const price = post.price ?? 0

  return createPortal(
    <>
      {/* Overlay backdrop */}
      <div
        className={`liquid-glass-overlay fixed inset-0 transition-all duration-300 ease-out ${
          closing
            ? "opacity-0 backdrop-blur-none"
            : entered
              ? "opacity-100 backdrop-blur-xl"
              : "opacity-0 backdrop-blur-none"
        }`}
        style={{ zIndex: Z.MODAL_NESTED, pointerEvents: closing || !entered ? "none" : "auto" }}
        onClick={requestClose}
        aria-hidden
      />

      {/* Centered modal container */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: Z.MODAL_NESTED + 1, pointerEvents: "none" }}
      >
        <div
          className="w-full max-w-md"
          style={{ ...panelStyle, pointerEvents: "auto" }}
        >
          <div className="liquid-glass-modal rounded-2xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <h2 className="text-base font-semibold text-foreground">
                付费加入{typeConfig.labelZh}库
              </h2>
              <button
                onClick={requestClose}
                className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5">
              {showError ? (
                <div className="flex flex-col items-center justify-center py-6 gap-4">
                  <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-500/20">
                    <AlertCircle size={28} strokeWidth={2.5} className="text-red-500" />
                  </div>
                  <div className="text-center">
                    <p className="text-base font-semibold text-foreground mb-1">
                      余额不足
                    </p>
                    <p className="text-sm text-muted-foreground">
                      您的账户余额不足以购买此{typeConfig.labelZh}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground/70 text-center">
                    请充值后再试
                  </p>
                </div>
              ) : (
                <>
                  {/* Ability preview */}
                  <div className="liquid-glass-surface rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate mb-1.5">
                          {post.title}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <AbilityTypeBadge type={post.abilityType} />
                          <span className="text-xs text-muted-foreground">
                            by {post.author.displayName}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Price display */}
                  <div className="flex flex-col items-center gap-2 mb-4">
                    <div className="text-2xl font-bold text-foreground">
                      ¥{price}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      一次付费，永久使用
                    </p>
                  </div>

                  {/* Trust badge */}
                  <div className="flex items-center justify-center gap-1.5 mb-2 text-xs text-muted-foreground/70">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    SafeClaw 安全支付
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-border shrink-0">
              {showError ? (
                <button
                  onClick={requestClose}
                  className="w-full h-9 flex items-center justify-center rounded-xl bg-secondary hover:bg-secondary/80 text-foreground text-sm font-medium transition-colors"
                >
                  知道了
                </button>
              ) : (
                <button
                  onClick={handlePay}
                  className={`w-full h-10 flex items-center justify-center gap-2 rounded-xl ${typeConfig.joinBgClass} text-white text-sm font-semibold transition-colors`}
                >
                  确认支付 ¥{price}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
